import React from 'react';
import ComboBoxFormik from '@/common/components/form/ComboBoxFormik';
import { useFormikContext } from 'formik';
import { usePaymentRules } from '../../customhook/usePaymentRules';
import { formatCurrency } from '@/common/helpers/mask.helpers';
import PixOrBoletoPayment from './PixOrBoletoPayment';
import CreditCardInformations from '../informations/CreditCardInformations';
import PixOrBoletoInformations from '../informations/PixOrBoletoInformatinos';
import CreditCardPayment from './CreditCardPayment';
import InputFormik from '@/common/components/form/InputFormik';

interface PaymentOptionsSectionProps {
  baseValue: number;
  amountCharged: number;
  finalValue: number;
  showBillingPeriod?: boolean;
}

export const PaymentOptionsSection: React.FC<PaymentOptionsSectionProps> = ({
  baseValue,
  showBillingPeriod = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formik = useFormikContext<any>();

  const currentEntryPercentage = formik.values.billingType === 'CREDIT_CARD'
    ? formik.values.creditCard.entryPercentage
    : formik.values.boletoOrPix.entryPercentage;

  const currentInstallments = formik.values.billingType === 'CREDIT_CARD'
    ? formik.values.creditCard.installments
    : formik.values.boletoOrPix.installments;

  const { rules, currentFees, installmentOptions, entryPercentageOptions } = usePaymentRules({
    totalValue: baseValue,
    paymentMethod: formik.values.billingType,
    installments: currentInstallments,
    entryPercentage: currentEntryPercentage
  });

  const paymentMethodOptions = React.useMemo(() => {
    return [
      { value: "", label: "Selecione o método" },
      { value: "PIX", label: "PIX" },
      { value: "BOLETO", label: "Boleto" },
      { value: "CREDIT_CARD", label: `Cartão de Crédito ${baseValue < 250 ? '(Apenas à vista)' : ''}` }
    ];
  }, [baseValue]);

  return (
    <section id="payment-options" className="space-y-6 mt-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        <section>
          {/* Regras de Pagamento */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              Regras de Pagamento
            </h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>Valor total:</strong> {formatCurrency(baseValue)}</p>

              {formik.values.billingType === 'CREDIT_CARD' && (
                <>
                  {baseValue < 250 ? (
                    <p className="text-orange-600">⚠️ Cartão de crédito: apenas à vista para valores abaixo de R$ 250,00</p>
                  ) : (
                    <p className="text-green-600">✅ Cartão de crédito: parcelamento até 12x com entrada opcional</p>
                  )}
                  <p><strong>Taxa:</strong> {currentFees.description} (aplicada apenas no valor do cartão)</p>
                </>
              )}

              {(formik.values.billingType === 'PIX' || formik.values.billingType === 'BOLETO') && (
                <>
                  <p><strong>Parcelamento:</strong> Parcela mínima R$ 50,00</p>
                  <p><strong>Entrada mínima:</strong> {rules.minEntryPercentage}% (mín. R$ 50,00)</p>
                  <p><strong>Taxa:</strong> Sem taxa</p>
                </>
              )}
            </div>
          </div>

          {/* Seleção de Método + Componentes de Pagamento */}
          <div className='mt-4 space-y-4'>
            <ComboBoxFormik
              label="Forma de pagamento"
              name="billingType"
              options={paymentMethodOptions}
              required={true}
              onChange={(value) => {
                formik.setFieldValue('billingType', value);
                if (value === 'CREDIT_CARD') {
                  formik.setFieldValue('creditCard.entryPercentage', '0');
                  formik.setFieldValue('creditCard.installments', 1);
                } else {
                  formik.setFieldValue('boletoOrPix.entryPercentage', '0');
                  formik.setFieldValue('boletoOrPix.installments', 1);
                }
              }}
            />

            {showBillingPeriod && (
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <ComboBoxFormik
                  name="billingPeriod"
                  label="Escolha o período da cobrança:"
                  options={[
                    { value: "", label: "-" },
                    { value: "mensal", label: "Mensal" },
                    { value: "semanal", label: "Semanal" },
                  ]}
                  required={true}
                />

                <InputFormik
                  label="Data de vencimento"
                  name='dueDate'
                  type="date"
                  required={true}
                />
              </div>
            )}

            {/* Componentes específicos por tipo de pagamento */}
            {formik.values.billingType === 'CREDIT_CARD' && (
              <CreditCardPayment
                formik={formik}
                rules={rules}
                entryPercentageOptions={entryPercentageOptions}
                installmentOptions={installmentOptions}
                currentFees={currentFees}
                baseValue={baseValue}
              />
            )}

            {(['PIX', 'BOLETO'].includes(formik.values.billingType)) && (
              <PixOrBoletoPayment
                formik={formik}
                rules={rules}
                entryPercentageOptions={entryPercentageOptions}
                installmentOptions={installmentOptions}
                baseValue={baseValue}
              />
            )}
          </div>
        </section>

        {/* Coluna de Informações/Resumos */}
        <div className="space-y-4">
          {formik.values.billingType === 'CREDIT_CARD' && (
            <CreditCardInformations
              formik={formik}
              rules={rules}
              baseValue={baseValue}
            />
          )}

          {(['PIX', 'BOLETO'].includes(formik.values.billingType)) && (
            <PixOrBoletoInformations
              formik={formik}
              baseValue={baseValue}
            />
          )}
        </div>
      </div>

      {/* Casos especiais */}
      {baseValue > 2500 && (
        <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
          <p className="text-sm text-orange-800">
            💼 <strong>Valor acima de R$ 2.500,00:</strong> Parcelamento sujeito à análise case a case.
            Entre em contato com o financeiro para aprovação.
          </p>
        </div>
      )}
    </section>
  );
};